import {
	type BooleanRuleKey,
	FUEL_MAX,
	findEntity,
	HEAT_WARNING,
	type MatchRules,
	type PlayerIndex,
	type PlayerState,
	SHIP_STRENGTH,
	type Ship,
	type ShipColor,
	TICK_MS,
	type WorldState,
} from "../sim";
import { el } from "./dom";

/** Optional resource bars, shown only when their rule is active. */
export type GaugeKey = "hull" | "fuel" | "laser";

/** Warning state of a gauge: running low (hull, fuel) or heating up (laser). */
export type GaugeAlert = "low" | "hot" | "overheated" | null;

interface GaugeDef {
	key: GaugeKey;
	label: string;
	rule: BooleanRuleKey;
	/** Fill level in [0, 1] for a ship. */
	value(ship: Ship, rules: MatchRules): number;
	alert(ship: Ship, value: number): GaugeAlert;
}

const lowAlert = (_: Ship, value: number): GaugeAlert => (value < 0.2 ? "low" : null);

/** Heat bar state: overheated (locked), hot (near the limit) or fine. */
export function heatAlert(ship: Ship, value: number): GaugeAlert {
	if (ship.laserOverheated) return "overheated";
	return value >= HEAT_WARNING ? "hot" : null;
}

const GAUGES: readonly GaugeDef[] = [
	{
		key: "hull",
		label: "Hull",
		rule: "damage",
		value: (s) => s.hull / SHIP_STRENGTH,
		alert: lowAlert,
	},
	{ key: "fuel", label: "Fuel", rule: "fuel", value: (s) => s.fuel / FUEL_MAX, alert: lowAlert },
	{
		key: "laser",
		label: "Heat",
		rule: "laserOverheat",
		value: (s, rules) => s.laserHeat / rules.laserHeatMax,
		alert: heatAlert,
	},
];

export interface PlayerHud {
	player: PlayerIndex;
	color: ShipColor;
	label: string;
	/** Ships remaining including the one flying or about to spawn. */
	lives: number;
	shipKills: number;
	asteroidKills: number;
	/** Active gauges and their fill (0 while the player has no ship). */
	gauges: Array<{ key: GaugeKey; label: string; value: number; alert: GaugeAlert }>;
	/** Missiles carried, or null when missiles are unlimited. */
	missiles: number | null;
	/** Active power-up effects. */
	powers: PowerChip[];
	/** Objective modes (P11.2): score, target and what the pilot is doing; null otherwise. */
	objective: { score: number; target: number; note: string } | null;
}

/** An active power-up effect on a pilot's ship. */
export interface PowerChip {
	key: "tripleLaser" | "warp" | "shield" | "homing" | "frozen";
	label: string;
	/** Seconds left, shots left or shield strength. */
	detail: string;
}

/** Whole seconds left, rounded up (the small slack absorbs whole-tick rounding: 667 ticks = 10 s). */
const seconds = (ticks: number) => `${Math.ceil((ticks * TICK_MS) / 1000 - 0.01)}s`;

/** The ship's active effects, in a fixed order. */
export function powerChips(ship: Ship, rules: MatchRules): PowerChip[] {
	const chips: PowerChip[] = [];
	if (ship.frozenTicks > 0) {
		chips.push({ key: "frozen", label: "Frozen", detail: seconds(ship.frozenTicks) });
	}
	if (ship.shield > 0) {
		const max = Math.max(ship.shield, rules.shieldHits);
		const detail =
			max <= 5 ? "●".repeat(ship.shield) + "○".repeat(max - ship.shield) : `${ship.shield}/${max}`;
		chips.push({ key: "shield", label: "Shield", detail });
	}
	if (ship.tripleLaserTicks > 0) {
		chips.push({ key: "tripleLaser", label: "Triple", detail: seconds(ship.tripleLaserTicks) });
	}
	if (ship.warpTicks > 0)
		chips.push({ key: "warp", label: "Warp", detail: seconds(ship.warpTicks) });
	if (ship.homingShots > 0) {
		chips.push({ key: "homing", label: "Homing", detail: `×${ship.homingShots}` });
	}
	return chips;
}

const LABELS: Record<ShipColor, string> = {
	blue: "Blue",
	red: "Red",
	green: "Green",
	yellow: "Yellow",
};

/** Score line for objective modes: "12 / 30", plus holding the hill or carrying a flag. */
function objectiveOf(world: WorldState, player: PlayerIndex): PlayerHud["objective"] {
	const o = world.objective;
	if (!o || o.kind === "survival") return null;
	const score = o.scores[player] ?? 0;
	let note = "";
	if (o.kind === "koth" && o.holder === player) note = "on the hill";
	if (o.kind === "ctf" && o.flags.some((f) => f.carrier === player)) note = "has the flag";
	return { score, target: o.target, note };
}

/** Ships a player still has: the queued/flying one plus those not yet spawned. */
export function livesOf(p: PlayerState): number {
	return p.shipsLeft + (p.shipId !== null || p.awaitingSpawn ? 1 : 0);
}

function shipOf(world: WorldState, p: PlayerState): Ship | undefined {
	const e = p.shipId === null ? undefined : findEntity(world, p.shipId);
	return e?.kind === "ship" ? e : undefined;
}

/**
 * HUD panels in screen order, matching the split-screen layout: with two players the
 * left panel is red (player 2) and the right panel blue (player 1).
 */
export interface HudOptions {
	/** Panel order left to right (default: red left, blue right with two players). */
	order?: PlayerIndex[] | undefined;
	/** Computer-controlled players, labelled "(AI)". */
	ai?: readonly PlayerIndex[];
	/** 3–4 viewports in a 2×2 grid: panels go to the top of each quarter. */
	quad?: boolean;
	/** Pilot names by player index ("" or missing = the ship colour, P11.8). */
	names?: readonly string[];
}

/** A pilot's display name: their own name, else the ship colour. */
export function pilotLabel(color: ShipColor, name?: string): string {
	return name || LABELS[color];
}

export function hudModel(world: WorldState, options: HudOptions = {}): PlayerHud[] {
	const gauges = GAUGES.filter((g) => world.rules[g.rule]);
	const panels = world.players.map((p): PlayerHud => {
		const ship = shipOf(world, p);
		return {
			player: p.index,
			color: p.color,
			label: `${pilotLabel(p.color, options.names?.[p.index])}${options.ai?.includes(p.index) ? " (AI)" : ""}`,
			lives: livesOf(p),
			shipKills: p.shipKills,
			// 2008 survival scoring shows its own score in the solo "Score" slot.
			asteroidKills:
				world.objective?.kind === "survival"
					? (world.objective.scores[p.index] ?? 0)
					: p.asteroidKills,
			gauges: gauges.map((g) => {
				const value = ship ? Math.min(1, Math.max(0, g.value(ship, world.rules))) : 0;
				return { key: g.key, label: g.label, value, alert: ship ? g.alert(ship, value) : null };
			}),
			missiles: world.rules.missilesPerLife !== null ? (ship?.missiles ?? 0) : null,
			powers: ship ? powerChips(ship, world.rules) : [],
			objective: objectiveOf(world, p.index),
		};
	});
	if (options.order) {
		return options.order.flatMap((i) => panels.filter((p) => p.player === i));
	}
	return options.ai?.length ? panels : panels.length > 1 ? [...panels].reverse() : panels;
}

interface Cell {
	lives: HTMLElement;
	ships: HTMLElement;
	rocks: HTMLElement;
	gauges: HTMLElement[];
	missiles: HTMLElement | null;
	powers: HTMLElement;
}

/** DOM overlay with one stats panel per viewport; only touches the DOM when values change. */
export class MatchHud {
	private readonly root: HTMLElement;
	private readonly cells: Cell[];
	private readonly last: string[] = [];

	private readonly options: HudOptions;

	constructor(parent: HTMLElement, world: WorldState, options: HudOptions = {}) {
		this.options = options;
		const model = hudModel(world, options);
		const solo = model.length === 1;
		const layout = solo ? " solo" : options.quad ? " quad" : "";
		this.root = el("div", { class: `match-hud${layout}`, "aria-hidden": "true" });
		this.cells = model.map((p) => {
			const lives = el("span", { class: "hud-lives" });
			const ships = el("span", { class: "hud-value" });
			const rocks = el("span", { class: "hud-value" });
			const gauges = p.gauges.map(() => el("span", { class: "hud-gauge-fill" }));
			const missiles = p.missiles === null ? null : el("span", { class: "hud-value" });
			const powers = el("span", { class: "hud-powers" });
			this.root.append(
				el(
					"div",
					{ class: `hud-panel hud-${p.color}` },
					el("span", { class: "hud-name" }, p.label),
					lives,
					solo ? null : el("span", { class: "hud-stat" }, "Kills ", ships),
					el("span", { class: "hud-stat" }, solo ? "Score " : "Asteroids ", rocks),
					missiles ? el("span", { class: "hud-stat" }, "Missiles ", missiles) : null,
					...p.gauges.map((g, i) =>
						el(
							"span",
							{ class: `hud-gauge hud-gauge-${g.key}` },
							g.label,
							el("span", { class: "hud-gauge-bar" }, gauges[i] ?? null),
						),
					),
					powers,
				),
			);
			return { lives, ships, rocks, gauges, missiles, powers };
		});
		parent.append(this.root);
		this.update(world);
	}

	update(world: WorldState): void {
		hudModel(world, this.options).forEach((p, i) => {
			const gaugeKey = p.gauges.map((g) => `${Math.round(g.value * 100)}${g.alert}`).join(",");
			const powerKey = p.powers.map((c) => `${c.key}${c.detail}`).join(",");
			const goal = p.objective ? `${p.objective.score}/${p.objective.note}` : "";
			const key = `${p.lives}|${p.shipKills}|${p.asteroidKills}|${p.missiles}|${gaugeKey}|${powerKey}|${goal}`;
			const cell = this.cells[i];
			if (!cell || this.last[i] === key) return;
			this.last[i] = key;
			if (p.objective) {
				// Unlimited ships: the score towards the target is what counts.
				const { score, target, note } = p.objective;
				cell.lives.textContent = `${score} / ${target}${note ? ` · ${note}` : ""}`;
				cell.lives.title = `${score} of ${target} points`;
				cell.lives.classList.add("hud-score");
			} else {
				cell.lives.textContent = "▲".repeat(Math.max(0, p.lives));
				cell.lives.title = `${p.lives} ships left`;
			}
			cell.ships.textContent = String(p.shipKills);
			cell.rocks.textContent = String(p.asteroidKills);
			if (cell.missiles) {
				cell.missiles.textContent = String(p.missiles ?? 0);
				cell.missiles.classList.toggle("empty", p.missiles === 0);
			}
			cell.powers.replaceChildren(
				...p.powers.map((c) =>
					el("span", { class: `hud-chip hud-chip-${c.key}` }, c.label, " ", el("b", {}, c.detail)),
				),
			);
			p.gauges.forEach((g, j) => {
				const fill = cell.gauges[j];
				if (!fill) return;
				fill.style.width = `${Math.round(g.value * 100)}%`;
				for (const alert of ["low", "hot", "overheated"] as const) {
					fill.classList.toggle(alert, g.alert === alert);
				}
			});
		});
	}

	destroy(): void {
		this.root.remove();
	}
}
